import { Api, ApiQueryOption } from '../../../shared/constants/api';
import { INavMenuItem, NavigationRoutingTree } from '../services/navigation/nav-structure';

export interface IAiChatNavRegistryEntry {
  template: string;
  description: string;
}

// Synthetic entries not tied to a real nav menu item — added alongside the tree-derived ones below.
const ADDITIONAL_AI_CHAT_NAV_ENTRIES: Record<string, IAiChatNavRegistryEntry> = {
  scanFindings: {
    template: `/${Api.scans}/${Api.scan_results}/{scanId}?${ApiQueryOption.linkedPropertyData}={digitalPropertyId}|{workspaceId}`,
    description:
      "The scan results page for one specific scan, which includes the list of findings for that scan. Use ONLY when the chat context includes a scanResults.scanId value (the user is viewing a specific scan's results) — fill {scanId} from that value. Findings are not filterable by scan on the general findings page, so this is the correct link for 'findings from this scan' requests. If no scanId is present in context, use the findings link instead. In every case, always mention the specific scan by name/date in your response text, since the response may be read later from a different page where this self-referential check no longer applies.",
  },
};

function buildTemplate(node: INavMenuItem): string {
  const path: string = (node.routerLink ?? node.overrideRouterLink) as string;
  const linkedPropertyDataSuffix: string = node.aiChatNav?.requiresLinkedPropertyData
    ? `?${ApiQueryOption.linkedPropertyData}={digitalPropertyId}|{workspaceId}`
    : '';

  return `${path}${linkedPropertyDataSuffix}`;
}

function collectAiChatNavEntries(nodes: INavMenuItem[], registry: Record<string, IAiChatNavRegistryEntry>): void {
  nodes.forEach((node: INavMenuItem): void => {
    if (node.aiChatNav && (node.routerLink || node.overrideRouterLink)) {
      registry[node.aiChatNav.key] = {
        template: buildTemplate(node),
        description: node.aiChatNav.description,
      };
    }

    if (node.children) {
      collectAiChatNavEntries(node.children, registry);
    }
  });
}

export const AI_CHAT_NAV_REGISTRY: Record<string, IAiChatNavRegistryEntry> = ((): Record<string, IAiChatNavRegistryEntry> => {
  const registry: Record<string, IAiChatNavRegistryEntry> = { ...ADDITIONAL_AI_CHAT_NAV_ENTRIES };
  collectAiChatNavEntries(NavigationRoutingTree, registry);
  return registry;
})();
